import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import ProgramHeader from '../../ReusableComponents/Header/ProgramHeader'
import Footer from '../../ReusableComponents/FooterComponent'
import TrainMachine from '../../AppMode/teachableMachine/TrainMachine'
import SegmentedControl from '../../ReusableComponents/SegmentedControl/SegmentedControl'
import InputsPanel from './InputsPanel'
import LayoutEditor from './LayoutEditor'
import LinesEditor from './LinesEditor'
import indicator from '../../../Assets/aiCode/indicator.svg'
import styles from './AiCodeConfigure.module.css'

// Configure step. A sidebar whose content switches: Inputs = camera/mic setup,
// A.I. = the teachable machine, Lines = crossing paths on the camera, Layout =
// the on-screen widget arrangement.
const BASE_TABS = [
    { slug: 'inputs', label: 'Inputs' },
    { slug: 'ai', label: 'A.I.' },
    { slug: 'lines', label: 'Lines' },
    { slug: 'layout', label: 'Outputs' },
]

const CONFIG_TAB_KEY = 'aiConfigureTab'

const AiCodeConfigure = () => {
    const history = useHistory()
    // Remember the last-opened sub-tab so returning to Configure lands on it.
    const [activeTab, setActiveTab] = useState(
        () => sessionStorage.getItem(CONFIG_TAB_KEY) || 'inputs'
    )
    const selectedFeatures = (() => {
        try {
            return (
                JSON.parse(
                    sessionStorage.getItem('aiCodeSelectedFeatures')
                ) || []
            )
        } catch {
            return []
        }
    })()
    // Derive availability from the LIVE selection (not the stale `enable`
    // snapshot set at goNext), so removing the mic/camera immediately drops the
    // matching training here — e.g. the Voice classes disappear when the mic is
    // removed (bug 24).
    const VISION_FEATURES = [
        'ai/face',
        'ai/motion',
        'ai/color',
        'ai/object',
        'ai/pose',
        'ai/gesture',
        'ai/tracking',
        'ai/ocr',
        'ai/marker',
    ]
    const hasImage =
        selectedFeatures.includes('inputs/camera') ||
        VISION_FEATURES.some((f) => selectedFeatures.includes(f))
    const hasAudio = selectedFeatures.includes('inputs/microphone')

    // Only these vision features actually need image training. Motion, colour,
    // tracking, OCR and markers use the camera but are algorithmic — so image
    // training must not appear for them.
    const TRAINABLE_VISION = ['ai/face', 'ai/object', 'ai/gesture', 'ai/pose']
    const hasTrainableVision =
        hasImage && selectedFeatures.some((f) => TRAINABLE_VISION.includes(f))
    // The A.I. (training) tab only makes sense when there's something to train.
    const showTraining = hasTrainableVision || hasAudio

    const [trainKind, setTrainKind] = useState(
        hasTrainableVision ? 'image' : 'audio'
    )
    const audioMode = hasAudio && (!hasTrainableVision || trainKind === 'audio')

    // The Lines tab (crossing paths) only applies when the Tracking A.I. feature
    // is selected (it also needs a camera).
    const hasTracking = hasImage && selectedFeatures.includes('ai/tracking')
    const TABS = BASE_TABS.filter((t) => {
        if (t.slug === 'lines') return hasTracking
        if (t.slug === 'ai') return showTraining
        return true
    })

    // Persist the active tab; if the saved tab is no longer available, snap back
    // to Inputs so the sidebar and content never disagree.
    useEffect(() => {
        if (!TABS.some((t) => t.slug === activeTab)) {
            setActiveTab('inputs')
            return
        }
        sessionStorage.setItem(CONFIG_TAB_KEY, activeTab)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, hasImage])

    // Forward/Back step through the Configure sub-tabs in order, only leaving the
    // Configure step at the ends (Back from the first tab -> Select Feature,
    // Next from the last tab -> Code). Users can also tap any tab in the sidebar.
    const tabIndex = TABS.findIndex((t) => t.slug === activeTab)
    const goBack = () => {
        if (tabIndex > 0) {
            setActiveTab(TABS[tabIndex - 1].slug)
            return
        }
        sessionStorage.setItem('slideDirection', true)
        history.push('/ai-code')
    }
    const goNext = () => {
        if (tabIndex >= 0 && tabIndex < TABS.length - 1) {
            setActiveTab(TABS[tabIndex + 1].slug)
            return
        }
        sessionStorage.removeItem('slideDirection')
        history.push('/aiCodeFlow')
    }

    const headerData = {
        selected: 1,
        options: [
            <p key="select-feature">Select Feature</p>,
            <p key="configure">Configure</p>,
            <p key="code">Code</p>,
            <p key="execute">Execute</p>,
        ],
    }

    const renderContent = () => {
        if (activeTab === 'ai') {
            return (
                <div className={styles.aiHost}>
                    {hasTrainableVision && hasAudio && (
                        <div className={styles.trainToggle}>
                            <SegmentedControl
                                options={[
                                    { value: 'image', label: 'Image Training' },
                                    { value: 'audio', label: 'Audio Training' },
                                ]}
                                value={trainKind}
                                onChange={setTrainKind}
                            />
                        </div>
                    )}
                    <div className={styles.trainHost}>
                        <TrainMachine
                            key={audioMode ? 'audio' : 'image'}
                            aiCodeMode
                            audioMode={audioMode}
                            hideHeader
                            onNext={goNext}
                        />
                    </div>
                </div>
            )
        }
        if (activeTab === 'inputs') {
            return <InputsPanel />
        }
        if (activeTab === 'lines') {
            return <LinesEditor />
        }
        if (activeTab === 'layout') {
            return <LayoutEditor />
        }
        return <p className={styles.placeholder}>Coming soon…</p>
    }

    return (
        <div className={styles.container}>
            <ProgramHeader data={headerData} showSave={true} showHelp={true} />

            <div className={styles.body}>
                <div className={styles.sidebar}>
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.slug
                        return (
                            <div
                                key={tab.slug}
                                className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ''
                                    }`}
                                onClick={() => setActiveTab(tab.slug)}
                            >
                                <div className={styles.sidebarHead}>
                                    <span>{tab.label}</span>
                                    {isActive && (
                                        <img
                                            className={styles.arrow}
                                            src={indicator}
                                            alt=""
                                        />
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className={styles.content}>{renderContent()}</div>
            </div>

            {/* The A.I. tab embeds TrainMachine which has its own footer nav. */}
            {/* {activeTab !== 'ai' && ( */}
            <div className={styles.footer}>
                <Footer
                    isGoBack={true}
                    goBack={goBack}
                    isGoNext={true}
                    goNext={goNext}
                />
            </div>
            {/* )} */}
        </div>
    )
}

export default AiCodeConfigure
